import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useSyncStore } from '../store/syncStore.js';

// ─── Conexión con la caja central ─────────────────────────────────────────────
//
// Una caja central (hub) es un servidor en la red local del restaurante que
// habla la misma API que la nube. Cuando la tablet está amarrada a un hub,
// TODAS las llamadas van al hub (client.js lee `hubUrl` de aquí) y además se
// abre un WebSocket para recibir cambios de cuentas, comandas y mesas al
// instante. Sin hub, la tablet habla con la nube directo, como siempre.

const DEVICE_KEY = 'gc-pos-device-id';
export const deviceId = (() => {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) { id = crypto.randomUUID(); localStorage.setItem(DEVICE_KEY, id); }
    return id;
  } catch { return 'unknown'; }
})();

export const useHubStore = create(
  persist(
    (set) => ({
      hubUrl:    null,   // "http://192.168.0.10:4747" — null = modo nube
      hubName:   null,
      connected: false,  // WebSocket vivo
      status:    null,   // último /hub/status o hub.status por WS
      embedded:  false,  // ESTA tablet es la caja: el hub corre dentro de la app
      hubId:     null,   // identidad de la caja (fija aunque cambie su IP)
      // Buscar sola la caja principal al iniciar sesión. Se apaga si alguien
      // desconecta a mano: no se vuelve a conectar sin que lo pida.
      autoEnlace: true,
      aviso:     null,   // mensaje corto para el usuario ("Conectado a …")

      setHub:      (hubUrl, hubName, hubId = null) => set({ hubUrl, hubName, hubId, autoEnlace: true }),
      setEmbedded: (embedded)        => set({ embedded }),
      clearHub: () => set({ hubUrl: null, hubName: null, hubId: null, connected: false, status: null, embedded: false }),
      setAutoEnlace: (autoEnlace)    => set({ autoEnlace }),
      avisar:   (aviso) => set({ aviso }),
      _set:     (patch) => set(patch),
    }),
    { name: 'gc-pos-hub', partialize: (s) => ({ hubUrl: s.hubUrl, hubName: s.hubName, embedded: s.embedded, hubId: s.hubId, autoEnlace: s.autoEnlace }) }
  )
);

export const hubUrl   = () => useHubStore.getState().hubUrl;
export const usingHub = () => Boolean(hubUrl());

// Verifica que en esa dirección haya un hub. Devuelve su estado o lanza.
export async function probeHub(url) {
  const clean = url.trim().replace(/\/+$/, '');
  const full  = /^https?:\/\//.test(clean) ? clean : `http://${clean}`;
  const withPort = /:\d+$/.test(new URL(full).host) ? full : `${full}:4747`;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 4000);
  try {
    const res  = await fetch(`${withPort}/hub/status`, { signal: ctrl.signal });
    const json = await res.json();
    if (!json?.data?.hub) throw new Error('Esa dirección no es una caja central');
    return { url: withPort, status: json.data };
  } finally { clearTimeout(t); }
}

// ─── Bus de eventos ───────────────────────────────────────────────────────────

const listeners = new Map();   // type → Set<fn>
export function onHubEvent(type, fn) {
  if (!listeners.has(type)) listeners.set(type, new Set());
  listeners.get(type).add(fn);
  return () => listeners.get(type)?.delete(fn);
}
const emit = (type, data) => { for (const fn of listeners.get(type) ?? []) { try { fn(data); } catch (e) { console.warn('[hub] listener', e); } } };

// ─── WebSocket ────────────────────────────────────────────────────────────────

let socket = null, retryTimer = null, retryMs = 1000, currentKey = null;

const applyStatus = (st) => {
  useHubStore.getState()._set({ status: st, hubName: st?.name ?? useHubStore.getState().hubName });
  const sync = useSyncStore.getState();
  sync.setOnline(Boolean(st?.cloud?.online));
  sync.setPending(st?.cloud?.pending ?? 0);
  sync.setErrors(st?.cloud?.dead ?? 0);
  if (st?.cloud?.lastSyncAt) sync.setSyncedAt();
};

function open(url, token) {
  const wsUrl = `${url.replace(/^http/, 'ws')}/ws?token=${encodeURIComponent(token)}`;
  try { socket = new WebSocket(wsUrl); } catch { scheduleRetry(url, token); return; }

  socket.onopen = () => {
    useHubStore.getState()._set({ connected: true });
    emit('connected', true);
  };
  socket.onmessage = (ev) => {
    let msg; try { msg = JSON.parse(ev.data); } catch { return; }
    if (msg.type === 'hello') {
      // La espera entre reintentos vuelve a empezar solo cuando el hub ya
      // aceptó la sesión (manda el "hello"), no al abrir el socket: el hub abre
      // y luego cierra con 4401/4503 si no reconoce el token, y reiniciarla en
      // onopen hacía que se reconectara una vez por segundo, sin fin.
      retryMs = 1000;
      applyStatus(msg.data.status);
      emit('tabs.snapshot',    msg.data.tabs);
      emit('kitchen',          msg.data.kitchen);
      emit('tables',           msg.data.tables);
      emit('session',          msg.data.status?.session ?? null);
      return;
    }
    if (msg.type === 'hub.status') applyStatus(msg.data);
    emit(msg.type, msg.data);
  };
  socket.onclose = (ev) => {
    useHubStore.getState()._set({ connected: false });
    emit('connected', false);
    // 4401 = el hub no reconoce el token. Suele ser temporal (la caja acaba de
    // arrancar o no tiene internet para verificarlo), así que se reintenta
    // con calma en vez de rendirse; si el token de verdad venció, el POS
    // pedirá login por su lado al recibir 401 en HTTP.
    // 4503 = la caja no tiene internet para verificar la sesión: lo mismo.
    if (ev.code === 4401 || ev.code === 4503) retryMs = Math.max(retryMs, 8000);
    scheduleRetry(url, token);
  };
  socket.onerror = () => { /* onclose se encarga */ };
}

function scheduleRetry(url, token) {
  clearTimeout(retryTimer);
  retryTimer = setTimeout(() => { if (currentKey === `${url}|${token}`) open(url, token); }, retryMs);
  retryMs = Math.min(retryMs * 2, 15000);
}

// Mantiene UNA conexión viva mientras haya hub + token. Idempotente.
export function ensureHubSocket(token) {
  const url = hubUrl();
  const key = url && token ? `${url}|${token}` : null;
  if (key === currentKey) return;
  currentKey = key;
  clearTimeout(retryTimer);
  if (socket) { const s = socket; socket = null; s.onclose = null; s.close(); }
  useHubStore.getState()._set({ connected: false });
  if (!key) { useSyncStore.getState().setOnline(navigator.onLine); useSyncStore.getState().setPending(0); return; }
  open(url, token);
}

// Si la app se sirvió desde el hub mismo (tablet abrió http://caja:4747), se
// amarra sola: no hay que teclear nada.
export async function autoDetectHub() {
  if (hubUrl()) return;
  if (import.meta.env?.DEV) return;
  try {
    const { url, status } = await probeHub(window.location.origin);
    useHubStore.getState().setHub(url, status.name);
    applyStatus(status);
  } catch { /* no es un hub */ }
}
